import React from "react";
import { COLORS } from "~/src/styles/config.style";

/**
 * A small carousel component to switch between multiple panels in a small space, with built-in navigation buttons and indicators.
 */
export const Carousel: React.FC<{
  panels: React.ReactNode[];
}> = ({ panels }) => {
  const [activePanelIndex, setActivePanelIndex] = React.useState(0);

  if (!panels.length) {
    return null;
  }

  const canGoPrev = activePanelIndex > 0;
  const canGoNext = activePanelIndex < panels.length - 1;
  const hasMultiplePanels = panels.length > 1;

  React.useEffect(() => {
    if (!panels.length) {
      setActivePanelIndex(0);
      return;
    }

    if (activePanelIndex > panels.length - 1) {
      setActivePanelIndex(panels.length - 1);
    }
  }, [activePanelIndex, panels.length]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {hasMultiplePanels ? (
          <button
            type="button"
            aria-label="Previous panel"
            onClick={() =>
              canGoPrev && setActivePanelIndex((index) => index - 1)
            }
            disabled={!canGoPrev}
            style={{
              border: "1px solid #d0d0d0",
              background: canGoPrev ? "#fff" : "#f2f2f2",
              color: canGoPrev ? COLORS.BLACK : COLORS.GRID_GREY,
              borderRadius: "4px",
              cursor: canGoPrev ? "pointer" : "not-allowed",
              width: "24px",
              height: "24px",
              lineHeight: "1",
              padding: 0,
            }}
          >
            &laquo;
          </button>
        ) : (
          <div style={{ width: "24px", height: "24px" }} />
        )}

        <div style={{ flex: 1, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              width: `${panels.length * 100}%`,
              transform: `translateX(-${(activePanelIndex * 100) / panels.length}%)`,
              transition: "transform 260ms ease",
            }}
          >
            {panels.map((panel, index) => (
              <div
                key={`panel-slide-${index}`}
                style={{
                  width: `${100 / panels.length}%`,
                  flexShrink: 0,
                }}
              >
                {panel}
              </div>
            ))}
          </div>
        </div>

        {hasMultiplePanels ? (
          <button
            type="button"
            aria-label="Next panel"
            onClick={() =>
              canGoNext && setActivePanelIndex((index) => index + 1)
            }
            disabled={!canGoNext}
            style={{
              border: "1px solid #d0d0d0",
              background: canGoNext ? "#fff" : "#f2f2f2",
              color: canGoNext ? COLORS.BLACK : COLORS.GRID_GREY,
              borderRadius: "4px",
              cursor: canGoNext ? "pointer" : "not-allowed",
              width: "24px",
              height: "24px",
              lineHeight: "1",
              padding: 0,
            }}
          >
            &raquo;
          </button>
        ) : (
          <div style={{ width: "24px", height: "24px" }} />
        )}
      </div>

      {hasMultiplePanels && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "6px",
            marginBottom: "10px",
          }}
        >
          {panels.map((_, index) => {
            const isActive = index === activePanelIndex;

            return (
              <button
                key={`panel-dot-${index}`}
                type="button"
                aria-label={`Go to panel ${index + 1}`}
                onClick={() => setActivePanelIndex(index)}
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  border: "1px solid #d0d0d0",
                  background: isActive ? COLORS.MAIN_ORANGE : COLORS.WARM_GREY,
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
